// Admin console entry (docs/features/admin-dashboard/phase-3-frontend-build-isolation.md
// in the backend repo). Reached ONLY from admin.html, which is the sole rollup
// input of `npm run build:admin` — the customer build never sees this file.
//
// Mirrors src/index.jsx's hard-won ordering, minus the customer-only concerns.
// Kept: the polyfills import first, the Chrome-translate guard before
// createRoot(...).render(...), the RUM error hooks, the error boundary.
// Dropped: locale bootstrapping, the locale-change handler, Mixpanel locale
// registration and the zod error map — the console is English-only (D13), so
// none of that has anything to configure here.

// Buffer global first — see the module's comment; import order matters here.
import './polyfills';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import AdminRoot from './modules/Admin/components/AdminRoot';
import ErrorPage from './modules/App/pages/ErrorPage';

import adminHttp from './modules/Admin/api/adminHttp';
import queryClient from './queryClient';

import './styles/main.css';
import { logRumError } from './modules/App/_rum';
import { installChromeTranslateGuard } from './modules/App/chromeTranslateGuard';

// The three modules/App imports above are deliberate and bounded: _rum,
// chromeTranslateGuard and pages/ErrorPage are infrastructure, not customer
// features. They are recorded as an allowed edge in scripts/lint-admin-isolation.js
// rather than exempted by a blanket rule — if any of them grows a
// customer-feature dependency the check fails, which is the point.

// Install BEFORE React renders, for the same reason as src/index.jsx: the
// Node.prototype patches must be in place by the time React touches the DOM.
// An admin is as likely to run Chrome Translate as a customer is.
installChromeTranslateGuard();

// Global error handler for uncaught exceptions. Same shape as src/index.jsx —
// `event.error` is null for cross-origin scripts and some extension
// content-script failures, so the fallback keeps `event.message` and the source
// location rather than throwing away the only identifying information those
// events carry.
const handleGlobalErrors = (event) => {
  const error =
    event.error ||
    new Error(
      `${event.message || 'Unknown error'} (${event.filename || 'unknown source'}:${
        event.lineno ?? '?'
      }:${event.colno ?? '?'})`,
    );
  logRumError(error, {
    source: 'window.onerror',
    isFatalError: true,
  });
  // NB this SUPPRESSES the browser's own console line for the uncaught error —
  // logRumError's console.error is the replacement.
  event.preventDefault();
};

window.addEventListener('error', handleGlobalErrors);

/**
 * Seed the country cache from the ADMIN API before anything renders (admin-api-split D7).
 *
 * `CountrySelectField` gets its options from `useAllCountries()` in
 * `modules/Common/utils/Utility.js`, which queries key `['countries-all']` through the
 * DEFAULT axios instance — i.e. the customer API. On this origin that is wrong twice over:
 * it is a second base URL from one page, and the customer `/country` route sits below
 * `checkTrialExpiration`, so it 403s for an admin whose Clerk session carries an
 * organisation with no active plan. That is what breaks the console's Country field today.
 *
 * `prefetchQuery`, NOT `setQueryData`, and the difference is a race rather than a style
 * preference. `setQueryData` writes data; if `useAllCountries` mounts first it has already
 * fired its own request at the customer API and the write lands too late to prevent it.
 * `prefetchQuery` creates the cache entry AND starts the fetch synchronously with the
 * queryFn below, so the hook joins this request instead of starting one. The 24h
 * `staleTime` matches the hook's, so the resolved data is fresh and never refetched.
 *
 * Deliberately NOT awaited: `root.render` below is synchronous by design (see the comment
 * there), and a slow or dead admin API must not white-screen the console — every other page
 * works without the country list. On failure the hook falls through to its own queryFn,
 * which is the pre-D7 behaviour, so this degrades to today rather than to something worse.
 *
 * The alternative — pointing `useAllCountries` at `adminHttp` — was rejected: it lives in
 * `modules/Common` and is shared with the customer app, so editing it changes both.
 */
queryClient
  .prefetchQuery({
    queryKey: ['countries-all'],
    queryFn: async () => (await adminHttp.get('/admin/country/all')).data,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
  .catch((error) => {
    // Not fatal, but not silent either: a console with no countries cannot create an
    // organisation, and the cause is worth naming when someone reports that.
    logRumError(error instanceof Error ? error : new Error(String(error)), {
      source: 'admin-index.seedCountries',
      isFatalError: false,
    });
  });

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

// Rendered synchronously, unlike src/index.jsx: there is no locale bundle to
// await, so there is nothing to defer the first paint behind.
root.render(
  <StrictMode>
    <ErrorBoundary
      FallbackComponent={ErrorPage}
      onError={(error, info) =>
        logRumError(error, {
          ...info,
          source: 'ErrorBoundary',
          isFatalError: true,
        })
      }
      onReset={() => {
        window.location.href = '/';
      }}
    >
      <AdminRoot />
    </ErrorBoundary>
  </StrictMode>,
);
